import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { addMinutes } from 'date-fns';
import { CredentialsModel } from 'ts/shared/classes/CredentialsModel';
import { getUserConfig } from './user';
import * as userService from './user';
import { User } from 'ts/shared/interfaces/User';
import { clearUser } from './cognito';

const refreshToken = async () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return;

    const user = JSON.parse(userStr);
    const response = await axios.post('/Account/RefreshToken', {
      refreshToken: user.refreshToken,
      usercode: user.userCode,
    });

    const newUser = {
      ...user,
      token: response.data.jwtToken,
    };
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + response.data.jwtToken;
    localStorage.setItem('user', JSON.stringify(newUser));

    return response.data.jwtToken;
  } catch (error) {
    console.log(error);
  }
};

export const configureAxiosInterceptorsB2B = async () => {
  axios.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const userStr = localStorage.getItem('user');

      if (userStr) {
        const user = JSON.parse(userStr);
        const defaultInactiveTime = 220 * 60 * 1000; // 2 hours
        const userInactiveTime = user.tokenLifetimeInMinutes * 60 * 1000 || defaultInactiveTime;
        const inactiveTime = userInactiveTime || defaultInactiveTime;
        const newUser = { ...user, latestUpdate: new Date().toString() };
        if (
          inactiveTime + new Date(user.latestUpdate).getTime() < new Date().getTime() &&
          user.isAuth
        ) {
          userService.cleanSessionStorage();
          localStorage.removeItem('user');
          window.location.href = '/login';
        } else {
          localStorage.setItem('user', JSON.stringify(newUser));
        }
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      if (error.response && error.response.status === 401) {
        const token = await refreshToken();

        if (token) {
          const originalRequest: any = error.config;

          originalRequest.headers.Authorization = `Bearer ${token}`;

          return axios(originalRequest);
        } else {
          userService.cleanSessionStorage();
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    },
  );
};

export const login = async (
  body: CredentialsModel,
  defaultUserSiteConfig: any,
): Promise<User | undefined> => {
  try {
    const url = 'Account/SignIn';
    const urlUser = 'Account/GetUserInfo';
    const data = (await axios.post(url, body)).data;
    if (data?.jwtToken) {
      axios.defaults.headers.common['Authorization'] = 'Bearer ' + data.jwtToken;

      const dataUser = (await axios.get(urlUser)).data;
      const userConfig = (await getUserConfig(defaultUserSiteConfig)) || {};
      const user: User = {
        ...dataUser,
        token: data.jwtToken,
        latestUpdate: new Date().toString(),
        isAuth: true,
        config: userConfig,
        refreshToken: data.refreshToken,
        tokenLifetimeInMinutes: data.tokenLifetimeInMinutes,
      };

      localStorage.setItem('user', JSON.stringify(user));
      return user;
    }
  } catch (err) {
    return undefined;
  }
};

export const logout = async (): Promise<void> => {
  try {
    const url = 'Account/SignOut';
    localStorage.removeItem('user');
    clearUser();
    await axios.get(url);
    delete axios.defaults.headers.common['Authorization'];

    await getUserToken();
  } catch (err) {
    return undefined;
  }
};

export const getUserToken = async (isAuthExpired: boolean = false): Promise<User | undefined> => {
  const urlUser = 'Account/GetUserInfo';
  const dataStr = localStorage.getItem('user');
  if (dataStr) {
    const data = JSON.parse(dataStr);
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + data.token;
    return data;
  } else {
    try {
      const res = await axios.get('Account/GetUserToken');
      if (res.data?.jwtToken) {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + res.data.jwtToken;
        const validTo = addMinutes(new Date(), res.data.lifetimeInMinutes).toString();
        const dataUser = (await axios.get(urlUser)).data;
        const user: User = {
          ...dataUser,
          token: res.data.jwtToken,
          isAuth: false,
          validTo,
          isAuthExpired,
        };
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }
    } catch (error) {
      return undefined;
    }
  }
};
